import { useEffect, useRef, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  type Identifier,
} from "ra-core";
import { CornerDownRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ContactNote } from "@/components/atomic-crm/types";
import {
  ThreadEditor,
  type ThreadDraftFile,
} from "@/modules/threads/ThreadEditor";
import type { ThreadTarget } from "@/modules/threads/threadTargets";
import type { ThreadNote } from "@/modules/threads/threadModel";
import { useInvalidateThreads } from "@/modules/threads/useRecordThreads";

type DataProvider = ReturnType<typeof useDataProvider>;

export const createThreadNote = (
  dataProvider: DataProvider,
  params: {
    target: Pick<ThreadTarget, "kind" | "id">;
    text: string;
    files?: ThreadDraftFile[];
    memberId?: Identifier;
    parentId?: Identifier;
    /** Nested reply: the reply being answered. */
    replyToId?: Identifier;
  },
) => {
  const base = {
    text: params.text.trim(),
    date: new Date().toISOString(),
    organization_member_id: params.memberId,
    parent_id: params.parentId ?? null,
    reply_to_id: params.replyToId ?? null,
    // Uploaded to storage by the note save hook.
    ...(params.files?.length ? { attachments: params.files } : {}),
  };
  return params.target.kind === "deal"
    ? dataProvider.create("deal_notes", {
        data: { deal_id: params.target.id, ...base },
      })
    : dataProvider.create<ContactNote>("contact_notes", {
        data: { contact_id: params.target.id, ...base } as Partial<ContactNote>,
      });
};

/**
 * Reply box that opens right under the note being answered (slides in and
 * centres itself). Esc or Cancel closes it.
 */
export const InlineReplyComposer = ({
  root,
  target,
  targetAuthor,
  onDone,
}: {
  root: ThreadNote;
  target: ThreadNote;
  targetAuthor: string;
  onDone: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const invalidate = useInvalidateThreads();
  const ref = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<ThreadDraftFile[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const send = async () => {
    if ((!text.trim() && !files.length) || saving) return;
    setSaving(true);
    try {
      await createThreadNote(dataProvider, {
        target:
          root.resource === "deal_notes"
            ? { kind: "deal", id: root.deal_id! }
            : { kind: "contact", id: root.contact_id! },
        text,
        files,
        memberId: identity?.id,
        parentId: root.id,
        replyToId:
          String(target.id) === String(root.id) ? undefined : target.id,
      });
      setText("");
      setFiles([]);
      onDone();
      await invalidate();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not reply", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={ref}
      className="animate-in fade-in slide-in-from-top-2 space-y-1.5 py-2 duration-200 motion-reduce:animate-none"
    >
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CornerDownRight className="size-3.5 text-primary" />
        Replying to{" "}
        <span className="font-medium text-foreground">{targetAuthor}</span>
        <span className="hidden sm:inline">· Esc to cancel</span>
      </p>
      <ThreadEditor
        value={text}
        onChange={setText}
        onSubmit={() => void send()}
        onCancel={onDone}
        autoFocus
        files={files}
        onFilesChange={setFiles}
        rows={1}
        ariaLabel="Reply"
        placeholder="Write a reply… type @ to mention"
        className="bg-card ring-2 ring-primary/30"
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={onDone}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7"
              disabled={(!text.trim() && !files.length) || saving}
              isLoading={saving}
              onClick={() => void send()}
            >
              Reply
            </Button>
          </>
        }
      />
    </div>
  );
};
